import { useCallback } from 'react'

import { DebouncedState, useDebouncedCallback } from 'use-debounce'
import { useSelector } from 'react-redux'

import { useAppDispatch } from 'src/Hooks/redux'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { setTiktokTracksToInitialState } from 'src/Redux/tiktokTrack-process'
import { DEBOUNCE_TIMER_1000 } from 'src/Constants/constants'
import {
  searchTikTokTrackAuth,
  searchTikTokTrackNoAuth,
} from 'src/Redux/tiktokTrack-process/api-actions'
import {
  enteredTrackUrlEvent,
  startedEnterTrackEvent,
} from 'src/Helpers/TagManager'
import { getIsAuth } from 'src/Redux/auth-process/credentialsSlice/selectors'

export const useTiktokTrackSearchForm = (): {
  setTracksToInitialStateHandler: () => void
  debouncedFindTrack: DebouncedState<(currentText: string) => void>
  trackSearchHandler: ({ searchInput }: { searchInput: string }) => void
} => {
  const dispatch = useAppDispatch()
  const isAuth = useSelector(getIsAuth)

  const setTracksToInitialStateHandler = useCallback((): void => {
    dispatch(setTiktokTracksToInitialState())
  }, [dispatch])

  const trackSearchHandler = ({
    searchInput: searchInputValue,
  }: {
    searchInput: string
  }): void => {
    if (isAuth) {
      void dispatch(
        searchTikTokTrackAuth({
          searchText: searchInputValue,
        }),
      )
    } else {
      void dispatch(
        searchTikTokTrackNoAuth({
          searchText: searchInputValue,
        }),
      )
    }
  }

  const debouncedFindTrack = useDebouncedCallback((currentText: string) => {
    if (currentText === '') {
      setTracksToInitialStateHandler()
    } else {
      trackSearchHandler({ searchInput: currentText })

      const eventsParams = {
        campaignPlatform: CampaignPlatformTypeEnum.TIKTOK,
        userId: null,
        isFirstCampaign: true,
        campaignNumber: 1,
      }
      if (currentText.includes('https://')) {
        enteredTrackUrlEvent(eventsParams)
      } else {
        startedEnterTrackEvent(eventsParams)
      }
    }
  }, DEBOUNCE_TIMER_1000)

  return {
    setTracksToInitialStateHandler,
    debouncedFindTrack,
    trackSearchHandler,
  }
}
